<template>
  <div class="app-shell" :class="{ 'shell-collapsed': isDesktopCollapsed && !isMobile }">
    <aside
      class="sidebar"
      :class="{
        collapsed: isDesktopCollapsed && !isMobile,
        mobile: isMobile,
        open: isMobileDrawerOpen
      }"
      aria-label="Admin sidebar"
    >
      <div class="sidebar-header">
        <router-link
          to="/"
          class="brand"
          :class="{ centered: isDesktopCollapsed && !isMobile }"
          @click="handleBrandClick"
        >
          <img
            class="brand-logo"
            :src="showSidebarText ? logoFull : logoSquare"
            :alt="showSidebarText ? 'DrMata Admin' : 'DrMata'"
          >
        </router-link>

        <button
          v-if="!isMobile && !isDesktopCollapsed"
          type="button"
          class="icon-button"
          aria-label="Toggle sidebar"
          @click="toggleDesktopSidebar"
        >
          <ChevronLeftIcon :size="18" />
        </button>

        <button
          v-else-if="isMobile"
          type="button"
          class="icon-button"
          aria-label="Close sidebar"
          @click="closeMobileSidebar"
        >
          <XIcon :size="18" />
        </button>
      </div>

      <div class="sidebar-content">
        <section
          v-for="section in filteredSections"
          :key="section.title"
          class="nav-section"
        >
          <p v-if="showSidebarText" class="nav-section__label">{{ section.title }}</p>
          <router-link
            v-for="item in section.items"
            :key="item.path"
            :to="item.path"
            class="nav-item"
            :class="{ active: isActive(item.path) }"
            :aria-label="item.name"
            @click="handleNavigationSelection"
          >
            <component :is="item.icon" class="nav-item__icon" :size="18" />
            <span v-if="showSidebarText" class="nav-item__label">{{ item.name }}</span>
          </router-link>
        </section>
      </div>

      <div class="sidebar-footer">
        <section
          v-if="showSidebarText"
          class="workspace-card"
          aria-label="Workspace summary"
        >
          <div class="workspace-card__role">{{ roleLabel }}</div>
          <div v-if="hasOrganization" class="workspace-card__org">{{ organizationLabel }}</div>
        </section>

        <div class="account-panel" :class="{ compact: !showSidebarText }">
          <div class="account-card">
            <div class="account-avatar">
              <CircleUserRoundIcon :size="18" />
            </div>
            <div v-if="showSidebarText" class="account-copy">
              <strong>{{ usernameLabel }}</strong>
            </div>
          </div>

          <button
            type="button"
            class="logout-button"
            :aria-label="showSidebarText ? 'Log out' : 'Log out of DrMata Admin'"
            @click="logOut"
          >
            <LogOutIcon :size="18" />
            <span v-if="showSidebarText">Log Out</span>
          </button>
        </div>
      </div>
    </aside>

    <button
      v-if="!isMobile && isDesktopCollapsed"
      type="button"
      class="sidebar-expand-button"
      aria-label="Expand sidebar"
      @click="toggleDesktopSidebar"
    >
      <ChevronRightIcon :size="18" />
    </button>

    <div class="shell-main" :class="{ 'shell-main--mobile': isMobile }">
      <header v-if="isMobile" class="mobile-header">
        <button
          type="button"
          class="icon-button"
          aria-label="Open menu"
          @click="toggleMobileSidebar"
        >
          <MenuIcon :size="18" />
        </button>

        <div class="mobile-header__copy">
          <span class="mobile-header__eyebrow">{{ roleLabel }}</span>
          <strong>{{ mobileHeaderTitle }}</strong>
        </div>

        <button
          type="button"
          class="icon-button"
          aria-label="Log out"
          @click="logOut"
        >
          <LogOutIcon :size="18" />
        </button>
      </header>

      <main class="content-area" :class="{ 'content-area--mobile': isMobile }">
        <slot></slot>
      </main>

      <nav v-if="isMobile" class="mobile-tabbar" aria-label="Primary navigation">
        <router-link
          v-for="item in mobileTabItems"
          :key="item.path"
          :to="item.path"
          class="mobile-tab"
          :class="{ active: isActive(item.path) }"
        >
          <component :is="item.icon" class="mobile-tab__icon" :size="20" />
          <span>{{ item.shortName || item.name }}</span>
        </router-link>

        <button
          v-if="hasSecondaryMobileNav"
          type="button"
          class="mobile-tab"
          :class="{ active: isMobileDrawerOpen }"
          @click="toggleMobileSidebar"
        >
          <MenuIcon class="mobile-tab__icon" :size="20" />
          <span>More</span>
        </button>
      </nav>
    </div>

    <div
      v-if="isMobile && isMobileDrawerOpen"
      class="sidebar-overlay"
      @click="closeMobileSidebar"
    ></div>
  </div>
</template>

<script>
import {
  MenuIcon,
  XIcon,
  LogOutIcon,
  WorkflowIcon,
  CircleUserRoundIcon,
  BuildingIcon,
  LayoutDashboardIcon,
  BriefcaseBusinessIcon,
  KeyIcon,
  RectangleEllipsis,
  AmphoraIcon,
  RouterIcon,
  HospitalIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-vue-next';
import { useAuthStore } from '@/stores/authStore';
import logoFull from '@/assets/DrMata/LOGO-DRMATA-Black.png';
import logoSquare from '@/assets/DrMata/LOGO-DRMATA-BlackSquare.png';

const SIDEBAR_STORAGE_KEY = 'drmata-admin-sidebar-collapsed';

export default {
  name: 'LeftNavBar',
  components: {
    MenuIcon,
    XIcon,
    LogOutIcon,
    WorkflowIcon,
    CircleUserRoundIcon,
    BuildingIcon,
    LayoutDashboardIcon,
    BriefcaseBusinessIcon,
    KeyIcon,
    RectangleEllipsis,
    AmphoraIcon,
    RouterIcon,
    HospitalIcon,
    UserIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  data() {
    return {
      isDesktopCollapsed: false,
      isMobile: false,
      isMobileDrawerOpen: false,
      username: '',
      orgname: '',
      userRole: '',
      logoFull,
      logoSquare,
      navSections: [
        {
          title: 'Overview',
          items: [
            { name: 'Dashboard', shortName: 'Home', path: '/', icon: LayoutDashboardIcon, allowedRoles: ['admin', 'clinic_admin'], mobilePrimary: true },
            { name: 'Dashboard', shortName: 'Home', path: '/state-dashboard', icon: LayoutDashboardIcon, allowedRoles: ['state_health_org'], mobilePrimary: true },
            { name: 'Clinic Comparison', shortName: 'Compare', path: '/state-clinic-comparison', icon: HospitalIcon, allowedRoles: ['state_health_org'], mobilePrimary: true },
            { name: 'Accounts', shortName: 'Accounts', path: '/users', icon: UserIcon, allowedRoles: ['admin', 'clinic_admin'], mobilePrimary: true },
            { name: 'Clinics / Hospitals', shortName: 'Clinics', path: '/clinics', icon: HospitalIcon, allowedRoles: ['admin', 'clinic_admin'], mobilePrimary: true },
          ],
        },
        {
          title: 'Administration',
          items: [
            { name: 'Organizations', path: '/org', icon: BuildingIcon, allowedRoles: ['admin'] },
            { name: 'State Benchmark', path: '/state-benchmark', icon: LayoutDashboardIcon, allowedRoles: ['admin'] },
            { name: 'State Access', path: '/state-access', icon: WorkflowIcon, allowedRoles: ['admin'] },
            { name: 'Single Setup', path: '/onboarding', icon: WorkflowIcon, allowedRoles: ['admin'] },
            { name: 'Bulk Setup', path: '/bulk-setup', icon: WorkflowIcon, allowedRoles: ['admin'] },
            { name: 'Audit Cases', path: '/audits', icon: BriefcaseBusinessIcon, allowedRoles: ['admin'] },
            { name: 'Feedback', path: '/feedback', icon: RectangleEllipsis, allowedRoles: ['admin'] },
            { name: 'Artifact Masks', path: '/artifact', icon: AmphoraIcon, allowedRoles: ['admin'] },
            { name: 'Integration', path: '/integrations', icon: KeyIcon, allowedRoles: ['admin'] },
            { name: 'Login Audits', path: '/login-audits', icon: RectangleEllipsis, allowedRoles: ['admin'] },
            { name: 'Health Pings', path: '/health-pings', icon: RouterIcon, allowedRoles: ['admin'] },
          ],
        },
      ],
    };
  },
  computed: {
    showSidebarText() {
      return this.isMobile || !this.isDesktopCollapsed;
    },
    hasOrganization() {
      const name = String(this.orgname || '').trim();
      if (!name) {
        return false;
      }

      const normalized = name.toLowerCase();
      return !['unknown', 'n/a', 'na', 'null', 'undefined', 'none'].includes(normalized);
    },
    organizationLabel() {
      return this.hasOrganization ? String(this.orgname).trim() : '';
    },
    usernameLabel() {
      return this.username || 'Admin account';
    },
    roleLabel() {
      if (this.userRole === 'clinic_admin') {
        return 'Clinic Admin';
      }

      if (this.userRole === 'admin') {
        return 'System Admin';
      }

      if (this.userRole === 'state_health_org') {
        return 'State Health Org';
      }

      return 'Signed in';
    },
    filteredSections() {
      return this.navSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => item.allowedRoles.includes(this.userRole)),
        }))
        .filter((section) => section.items.length);
    },
    allNavItems() {
      return this.filteredSections.flatMap((section) => section.items);
    },
    mobileTabItems() {
      return this.allNavItems.filter((item) => item.mobilePrimary).slice(0, 4);
    },
    hasSecondaryMobileNav() {
      return this.allNavItems.some((item) => !item.mobilePrimary);
    },
    mobileHeaderTitle() {
      const active = this.allNavItems.find((item) => this.isActive(item.path));
      return active?.name || this.organizationLabel || this.roleLabel || 'DrMata';
    },
  },
  watch: {
    '$route.path'() {
      if (this.isMobile) {
        this.closeMobileSidebar();
      }
    },
  },
  mounted() {
    this.loadSidebarPreference();
    this.checkViewport();
    this.hydrateUser();
    window.addEventListener('resize', this.checkViewport);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkViewport);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    loadSidebarPreference() {
      this.isDesktopCollapsed = localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    },
    persistSidebarPreference() {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(this.isDesktopCollapsed));
    },
    toggleDesktopSidebar() {
      this.isDesktopCollapsed = !this.isDesktopCollapsed;
      this.persistSidebarPreference();
    },
    toggleMobileSidebar() {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
    },
    closeMobileSidebar() {
      this.isMobileDrawerOpen = false;
    },
    handleBrandClick() {
      if (this.isMobile) {
        this.closeMobileSidebar();
      }
    },
    handleNavigationSelection() {
      if (this.isMobile) {
        this.closeMobileSidebar();
      }
    },
    checkViewport() {
      this.isMobile = window.innerWidth < 992;
      if (!this.isMobile) {
        this.isMobileDrawerOpen = false;
      }
    },
    isActive(path) {
      if (path === '/') {
        return this.$route.path === '/';
      }

      return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
    },
    applyUserDetails(userDetails) {
      this.username = userDetails?.Username || '';
      this.orgname = userDetails?.OrganizationName || '';
      this.userRole = userDetails?.Role || '';
    },
    async hydrateUser() {
      const authStore = useAuthStore();

      try {
        await authStore.initializeSession();

        if (authStore.userDetails && Object.keys(authStore.userDetails).length) {
          this.applyUserDetails(authStore.userDetails);
          return;
        }

        if (authStore.accessToken) {
          const userDetails = await authStore.fetchUserDetails({ force: true });
          this.applyUserDetails(userDetails);
        }
      } catch (error) {
        console.error('Unable to hydrate sidebar user state.', error);
        authStore.logout({ redirectToLogin: true });
      }
    },
    async handleVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const authStore = useAuthStore();
      const hasSession = await authStore.ensureValidSession();
      if (!hasSession) {
        authStore.logout({ redirectToLogin: true });
        return;
      }

      if (!this.userRole) {
        await this.hydrateUser();
      }
    },
    async logOut() {
      const authStore = useAuthStore();
      authStore.logout();
      this.$router.push('/login');
    },
  },
};
</script>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
  background: var(--background-color, #ffffff);
  color: #1a1f2e;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
}

.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  width: 248px;
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(180deg, #faf7fc 0%, #f5f3f8 48%, #f3f4f7 100%);
  border-right: 1px solid rgba(110, 43, 146, 0.08);
  box-shadow: 1px 0 0 rgba(255, 255, 255, 0.6) inset;
  transition: width 0.22s ease, transform 0.22s ease;
  z-index: 20;
}

.sidebar.collapsed {
  width: 76px;
}

.sidebar.mobile {
  transform: translateX(-100%);
  width: min(82vw, 248px);
}

.sidebar.mobile.open {
  transform: translateX(0);
}

.sidebar-header,
.sidebar-footer {
  padding: 16px 14px;
  border-bottom: 1px solid rgba(110, 43, 146, 0.06);
}

.sidebar-footer {
  border-bottom: none;
  border-top: 1px solid rgba(110, 43, 146, 0.08);
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.brand {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.brand.centered {
  justify-content: center;
}

.brand-logo {
  width: auto;
  max-width: 140px;
  height: 80px;
  display: block;
  object-fit: contain;
  object-position: center;
}

.collapsed .brand-logo {
  max-width: 36px;
  height: 36px;
}

.icon-button {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid rgba(110, 43, 146, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: #4a4458;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(26, 31, 46, 0.05);
  transition: border-color 0.18s ease, color 0.18s ease, background-color 0.18s ease, transform 0.18s ease;
}

.icon-button svg,
.sidebar-expand-button svg {
  display: block;
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.icon-button:hover,
.icon-button:focus-visible {
  border-color: rgba(186, 111, 215, 0.45);
  color: var(--primary-dark, #6e2b92);
  background: rgba(186, 111, 215, 0.1);
  outline: none;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}

.sidebar:not(.mobile):not(.collapsed) .sidebar-header {
  padding-right: 54px;
}

.sidebar:not(.mobile):not(.collapsed) .sidebar-header .icon-button {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
}

.sidebar.collapsed .sidebar-header {
  justify-content: center;
  padding-inline: 12px;
}

.sidebar-content {
  flex: 1;
  padding: 12px 10px 20px;
  overflow-y: auto;
}

.workspace-card {
  margin: 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(110, 43, 146, 0.1);
  box-shadow: 0 1px 2px rgba(26, 31, 46, 0.03);
}

.workspace-card__role {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--primary-dark, #6e2b92);
  line-height: 1.3;
}

.workspace-card__org {
  margin-top: 4px;
  font-size: 0.86rem;
  font-weight: 650;
  line-height: 1.35;
  color: #1a1f2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.workspace-card:not(:has(.workspace-card__org)) {
  padding-block: 12px;
}

.nav-section + .nav-section {
  margin-top: 18px;
}

.nav-section__label {
  margin: 0 10px 8px;
  font-size: 0.65rem;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b8498;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 3px 4px;
  padding: 11px 14px;
  min-height: 44px;
  border-radius: 10px;
  color: #4f5668;
  text-decoration: none;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.nav-item:hover,
.nav-item:focus-visible {
  background: rgba(186, 111, 215, 0.08);
  color: #1a1f2e;
  outline: none;
}

.nav-item.active {
  background: rgba(186, 111, 215, 0.14);
  color: var(--primary-dark, #6e2b92);
  font-weight: 600;
  box-shadow: none;
}

.nav-item.active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--primary-color, #ba6fd7);
}

.nav-item__icon {
  flex-shrink: 0;
  color: inherit;
  stroke-width: 1.85;
}

.nav-item__label {
  font-size: 0.84rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.collapsed .nav-item {
  justify-content: center;
  padding-inline: 0;
}

.collapsed .nav-item.active::before {
  top: 8px;
  bottom: 8px;
}

.account-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(110, 43, 146, 0.08);
}

.account-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(186, 111, 215, 0.12);
  color: var(--primary-dark, #6e2b92);
  flex-shrink: 0;
}

.account-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.account-copy strong {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1a1f2e;
}

.account-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.account-panel.compact {
  align-items: center;
}

.account-panel.compact .account-card {
  padding: 8px;
}

.logout-button {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(220, 54, 46, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--error-color, #dc362e);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.logout-button:hover,
.logout-button:focus-visible {
  background: rgba(220, 54, 46, 0.08);
  border-color: rgba(220, 54, 46, 0.2);
  color: #b42318;
  outline: none;
}

.shell-main {
  flex: 1;
  min-width: 0;
  margin-left: 248px;
  transition: margin-left 0.22s ease;
}

.shell-collapsed .shell-main {
  margin-left: 76px;
}

.content-area {
  min-height: 100vh;
}

.content-area--mobile {
  min-height: calc(100dvh - 118px);
  padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
}

.mobile-header {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 10px 14px;
  padding-top: calc(10px + env(safe-area-inset-top, 0px));
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid rgba(23, 32, 51, 0.08);
  backdrop-filter: blur(14px);
}

.mobile-header__copy {
  min-width: 0;
  flex: 1;
}

.mobile-header__copy strong {
  display: block;
  font-size: 0.95rem;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-header__eyebrow {
  display: block;
  margin-bottom: 2px;
  font-size: 0.68rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #667085;
}

.mobile-tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 18;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 2px;
  padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid rgba(23, 32, 51, 0.08);
  backdrop-filter: blur(14px);
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
}

.mobile-tab {
  min-width: 0;
  min-height: 52px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #64748b;
  text-decoration: none;
  font-size: 0.68rem;
  font-weight: 650;
  line-height: 1;
  padding: 6px 4px;
}

.mobile-tab__icon {
  display: block;
}

.mobile-tab.active {
  color: var(--primary-dark, #6e2b92);
  background: rgba(186, 111, 215, 0.12);
}

.mobile-tab span {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(2px);
  z-index: 15;
}

.sidebar-expand-button {
  position: fixed;
  top: 26px;
  left: 58px;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(110, 43, 146, 0.1);
  border-radius: 10px;
  background: #f8fafc;
  color: #4a4458;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  z-index: 24;
  transition: border-color 0.18s ease, color 0.18s ease, background-color 0.18s ease;
}

.sidebar-expand-button:hover,
.sidebar-expand-button:focus-visible {
  border-color: rgba(186, 111, 215, 0.45);
  color: var(--primary-dark, #6e2b92);
  background: #ffffff;
  outline: none;
}

@media (max-width: 991.98px) {
  .shell-main,
  .shell-collapsed .shell-main {
    margin-left: 0;
  }

  .sidebar.mobile {
    width: min(88vw, 300px);
    z-index: 30;
  }

  .sidebar-overlay {
    z-index: 25;
  }

  .sidebar-expand-button {
    display: none;
  }

  .brand-logo {
    height: 52px;
    max-width: 120px;
  }

  .nav-item {
    min-height: 46px;
    padding: 12px 14px;
  }
}
</style>
